import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import {
  collection, query, where, getDocs, updateDoc, doc, orderBy, limit, writeBatch
} from 'firebase/firestore';
import './Notifications.css';

const TYPE_LABELS = {
  new_application: '新しい応募',
  application_approved: '応募が承認されました',
  application_rejected: '応募が拒否されました',
  application_cancelled: '応募がキャンセルされました',
  recruitment_full: '募集が定員に達しました',
  hiking_plan_reminder: '登山計画書の確認',
  group_chat_dissolved: 'グループチャットが解散されました',
  report_received: '通報を受け付けました',
  report_action_completed: '通報対応が完了しました',
  account_suspension: 'アカウント制限のお知らせ'
};

function Notifications() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    try {
      const q = query(
        collection(db, 'notifications'),
        where('recipientId', '==', currentUser.uid),
        orderBy('createdAt', 'desc'),
        limit(50)
      );
      const snap = await getDocs(q);
      setNotifications(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('通知の取得に失敗しました:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    fetchNotifications();
  }, [currentUser, fetchNotifications]);

  const markAllRead = async () => {
    const unread = notifications.filter(n => !n.isRead);
    if (unread.length === 0) return;
    const batch = writeBatch(db);
    unread.forEach(n => batch.update(doc(db, 'notifications', n.id), { isRead: true }));
    await batch.commit();
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
  };

  const getNotificationTarget = (notification) => {
    if (notification.type === 'group_chat_dissolved' && notification.recruitmentId) {
      return {
        pathname: `/group-chat/${notification.recruitmentId}`,
        state: {
          fromNotification: true,
          notificationType: notification.type
        }
      };
    }

    if (!notification.recruitmentId) return null;

    if (
      notification.type === 'application_approved' ||
      notification.type === 'application_rejected' ||
      notification.type === 'application_cancelled'
    ) {
      return {
        pathname: `/recruitments/${notification.recruitmentId}`,
        state: {
          fromNotification: true,
          notificationType: notification.type
        }
      };
    }

    return {
      pathname: `/recruitments/${notification.recruitmentId}`
    };
  };

  const handleClick = async (notification) => {
    if (!notification.isRead) {
      await updateDoc(doc(db, 'notifications', notification.id), { isRead: true });
      setNotifications(prev => prev.map(n => n.id === notification.id ? { ...n, isRead: true } : n));
    }
    const target = getNotificationTarget(notification);
    if (target) {
      navigate(target.pathname, { state: target.state });
    }
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  if (loading) return <div className="loading">読み込み中...</div>;

  return (
    <div className="notifications-container">
      <div className="notifications-header">
        <h1>通知</h1>
        {unreadCount > 0 && (
          <button className="btn btn-outline btn-sm" onClick={markAllRead}>
            すべて既読にする ({unreadCount})
          </button>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="notifications-empty">
          <p>通知はありません</p>
        </div>
      ) : (
        <div className="notifications-list">
          {notifications.map(n => (
            <div
              key={n.id}
              className={`notification-item ${n.isRead ? 'read' : 'unread'}`}
              onClick={() => handleClick(n)}
            >
              <div className="notification-dot" />
              <div className="notification-body">
                <p className="notification-type">{TYPE_LABELS[n.type] || n.type}</p>
                <p className="notification-message">
                  {n.senderId ? (
                    <span
                      className="notification-sender-link"
                      style={{fontWeight:'bold', textDecoration:'underline', cursor:'pointer'}}
                      onClick={(e) => { e.stopPropagation(); navigate(`/users/${n.senderId}`); }}
                    >
                      {n.senderName}
                    </span>
                  ) : null}
                  {n.senderId ? n.message.replace(n.senderName, '') : n.message}
                </p>
                <p className="notification-time">
                  {n.createdAt?.toDate
                    ? n.createdAt.toDate().toLocaleDateString('ja-JP', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                    : ''}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Notifications;
